<script setup>
import { computed, onMounted, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import subDays from 'date-fns/subDays';
import { getUnixEndOfDay, getUnixStartOfDay } from 'helpers/DateHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import { DATE_RANGE_OPTIONS } from 'dashboard/routes/dashboard/settings/reports/constants';
import ReportsFiltersAgents from 'dashboard/routes/dashboard/settings/reports/components/Filters/Agents.vue';
import ReportsFiltersDateRange from 'dashboard/routes/dashboard/settings/reports/components/Filters/DateRange.vue';

const props = defineProps({
  // Null while a fetch is in flight so stale counts are never shown.
  totalCount: {
    type: Number,
    default: null,
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  // Self-scoped viewers only ever see their own calls, so the assignee filter
  // is meaningless for them — only admins get it.
  showAssignee: {
    type: Boolean,
    default: false,
  },
});

const activity = defineModel('activity', { type: String, default: null });
const assigneeIds = defineModel('assigneeIds', {
  type: Array,
  default: () => [],
});
const inboxId = defineModel('inboxId', { type: Number, default: null });
const since = defineModel('since', { type: Number, default: null });
const untilDateTime = defineModel('until', { type: Number, default: null });

const { t } = useI18n();

const ACTIVITY_ICONS = {
  missed: 'i-lucide-phone-missed',
  no_reply: 'i-lucide-phone-outgoing',
  incoming: 'i-lucide-phone-incoming',
  outgoing: 'i-lucide-phone-outgoing',
  in_progress: 'i-lucide-phone-call',
};

const BASE_ACTIVITIES = ['missed', 'no_reply'];
const OTHER_ACTIVITIES = ['incoming', 'outgoing', 'in_progress'];

// A single open-menu identifier keeps the dropdowns mutually exclusive:
// opening one closes the others without any cross-wiring.
const openMenu = ref(null); // 'activity' | 'more' | null
const hasInitialDateRange = Boolean(since.value && untilDateTime.value);
const selectedDateRange = ref(
  hasInitialDateRange
    ? DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE
    : DATE_RANGE_OPTIONS.TODAY
);
const customDateRange = ref(
  hasInitialDateRange
    ? [new Date(since.value * 1000), new Date(untilDateTime.value * 1000)]
    : [new Date(), new Date()]
);

const isCustomDateRange = computed(
  () => selectedDateRange.value.id === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id
);

const applyDateRange = () => {
  const isCustom = isCustomDateRange.value;
  const toDate = isCustom ? customDateRange.value[1] : new Date();
  const fromDate = isCustom
    ? customDateRange.value[0]
    : subDays(new Date(), selectedDateRange.value.offset);
  since.value = getUnixStartOfDay(fromDate);
  untilDateTime.value = getUnixEndOfDay(toDate);
};

const onDateRangeChange = selectedRange => {
  selectedDateRange.value = selectedRange;
  applyDateRange();
};

const onCustomDateRangeChange = value => {
  customDateRange.value = value;
  applyDateRange();
};

onMounted(() => {
  if (!hasInitialDateRange) applyDateRange();
});

const toggleMenu = name => {
  openMenu.value = openMenu.value === name ? null : name;
};

// Each dropdown wrapper closes itself on outside clicks. The guard keeps the
// other two wrappers (which the click is also outside of) from closing a
// menu the user is interacting with.
const closeOnOutside = name => {
  if (openMenu.value === name) openMenu.value = null;
};

const activityLabel = value => t(`CALLS_PAGE.FILTERS.${value.toUpperCase()}`);

const activeChipLabel = computed(() => {
  const label = activityLabel(activity.value);
  return props.totalCount === null ? label : `${label} (${props.totalCount})`;
});

const inactiveChips = computed(() =>
  BASE_ACTIVITIES.filter(value => value !== activity.value)
);

const otherActivityItems = computed(() =>
  OTHER_ACTIVITIES.map(value => ({
    label: activityLabel(value),
    value,
    action: 'filter',
    icon: ACTIVITY_ICONS[value],
    isSelected: activity.value === value,
  }))
);

const moreFiltersSections = computed(() => [
  {
    title: t('CALLS_PAGE.FILTERS.INBOX'),
    items: [
      {
        label: t('CALLS_PAGE.FILTERS.ALL_INBOXES'),
        value: null,
        action: 'inbox',
        isSelected: !inboxId.value,
      },
      ...props.inboxes.map(inbox => ({
        label: inbox.name,
        value: inbox.id,
        action: 'inbox',
        isSelected: inboxId.value === inbox.id,
      })),
    ],
  },
]);

const hasMoreFilters = computed(() => Boolean(inboxId.value));

const setActivity = value => {
  openMenu.value = null;
  activity.value = value;
};

const setAssignees = agents => {
  assigneeIds.value = agents.map(agent => agent.id);
};

const applyMoreFilter = ({ action, value }) => {
  openMenu.value = null;
  if (action === 'inbox') inboxId.value = value;
};
</script>

<template>
  <div class="flex flex-wrap items-center justify-between gap-3">
    <div class="flex flex-wrap items-center gap-3">
      <span v-if="!activity" class="text-heading-3 text-n-slate-11 shrink-0">
        {{
          totalCount === null
            ? t('CALLS_PAGE.ALL_CALLS')
            : t('CALLS_PAGE.ALL_CALLS_COUNT', { count: totalCount })
        }}
      </span>
      <Button
        v-else
        variant="outline"
        color="blue"
        size="sm"
        :icon="ACTIVITY_ICONS[activity]"
        class="shrink-0"
        @click="setActivity(null)"
      >
        {{ activeChipLabel }}
        <Icon icon="i-lucide-x" />
      </Button>
      <div class="w-px h-4 bg-n-strong shrink-0" />
      <Button
        v-for="chip in inactiveChips"
        :key="chip"
        variant="outline"
        color="slate"
        size="sm"
        :icon="ACTIVITY_ICONS[chip]"
        :label="activityLabel(chip)"
        class="shrink-0 text-n-slate-12"
        @click="setActivity(chip)"
      />
      <OnClickOutside
        class="relative shrink-0"
        @trigger="closeOnOutside('activity')"
      >
        <Button
          variant="outline"
          color="slate"
          size="sm"
          icon="i-lucide-phone"
          class="text-n-slate-12"
          @click="toggleMenu('activity')"
        >
          {{ t('CALLS_PAGE.FILTERS.OTHER_ACTIVITY') }}
          <Icon icon="i-lucide-chevron-down" class="text-n-slate-11" />
        </Button>
        <DropdownMenu
          v-if="openMenu === 'activity'"
          :menu-items="otherActivityItems"
          class="mt-1 start-0 top-full w-44"
          @action="setActivity($event.value)"
        />
      </OnClickOutside>
    </div>
    <div class="flex items-center gap-2 shrink-0">
      <ReportsFiltersAgents
        v-if="showAssignee"
        class="w-64"
        @agents-filter-selection="setAssignees"
      />
      <div class="flex items-center gap-2">
        <ReportsFiltersDateRange
          :default-date-range="selectedDateRange.id"
          class="w-56"
          @on-range-change="onDateRangeChange"
        />
        <WootDateRangePicker
          v-if="isCustomDateRange"
          show-range
          class="no-margin auto-width"
          :value="customDateRange"
          :confirm-text="t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
          :placeholder="t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
          @change="onCustomDateRangeChange"
        />
      </div>
      <OnClickOutside class="relative" @trigger="closeOnOutside('more')">
        <Button
          variant="outline"
          size="sm"
          icon="i-lucide-list-filter"
          :color="hasMoreFilters ? 'blue' : 'slate'"
          :class="hasMoreFilters ? '' : 'text-n-slate-12'"
          @click="toggleMenu('more')"
        >
          {{ t('CALLS_PAGE.FILTERS.MORE_FILTERS') }}
          <Icon
            icon="i-lucide-chevron-down"
            :class="hasMoreFilters ? '' : 'text-n-slate-11'"
          />
        </Button>
        <DropdownMenu
          v-if="openMenu === 'more'"
          :menu-sections="moreFiltersSections"
          class="mt-1 end-0 top-full w-56 max-h-80"
          @action="applyMoreFilter"
        />
      </OnClickOutside>
    </div>
  </div>
</template>
